"use client";
import { useEffect } from "react";
import { usePathname, useSearchParams } from "next/navigation";
import useAuthStore from "@/stores/useAuthStore";

declare global {
  interface Window {
    fbq?: (...args: any[]) => void;
    _fbq?: any;
    __META_PIXEL_BASE_INIT__?: boolean;
    __META_PIXEL_LAST_AM__?: string;
  }
}

const PIXEL_ID = process.env.NEXT_PUBLIC_PIXEL_META_ID;

// Carga segura del script de Meta Pixel (si no existe)
function ensureFbqLoaded() {
  if (typeof window === "undefined") return;
  if (typeof window.fbq === "function") return;

  (function (f: Window & typeof globalThis, b: Document, e: string, v: string) {
    let n: any;
    if ((f as any).fbq) return;

    n = (f as any).fbq = function (...args: any[]) {
      n.callMethod ? n.callMethod.apply(n, args) : n.queue.push(args);
    };

    if (!(f as any)._fbq) (f as any)._fbq = n;
    n.push = n;
    n.loaded = true;
    n.version = "2.0";
    n.queue = [];

    const tEl = b.createElement(e) as HTMLScriptElement;
    tEl.async = true;
    tEl.src = v;

    const sEl = b.getElementsByTagName(e)[0];
    if (sEl && sEl.parentNode) {
      sEl.parentNode.insertBefore(tEl, sEl);
    } else {
      b.head?.appendChild(tEl);
    }
  })(window, document, "script", "https://connect.facebook.net/en_US/fbevents.js");
}

const buildAdvancedMatching = (userData: any, userMetadata: any) => {
  if (!userData && !userMetadata) return null;

  const email = userData?.email;
  const firstName = userMetadata?.first_name;
  const lastName = userMetadata?.last_name;
  const birthdate = userMetadata?.birthdate;
  const country = userMetadata?.country;
  const externalId = userData?.id ?? userMetadata?.user_id;
  const phone = userMetadata?.phone;

  const payload: Record<string, string> = {};

  if (email) payload.em = String(email).toLowerCase();
  if (firstName) payload.fn = String(firstName).toLowerCase();
  if (lastName) payload.ln = String(lastName).toLowerCase();

  if (birthdate) {
    const d = new Date(birthdate);
    if (!isNaN(d.getTime())) {
      const yyyy = d.getUTCFullYear();
      const mm = String(d.getUTCMonth() + 1).padStart(2, "0");
      const dd = String(d.getUTCDate()).padStart(2, "0");
      payload.db = `${yyyy}${mm}${dd}`;
    }
  }

  if (country) payload.country = String(country).toLowerCase();
  if (externalId) payload.external_id = String(externalId);
  if (phone) {
    const phoneDigits = String(phone).replace(/\D/g, "");
    if (phoneDigits) payload.ph = phoneDigits;
  }

  return Object.keys(payload).length > 0 ? payload : null;
};

export default function PixelMeta() {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const userData = useAuthStore((state) => state.userData);
  const userMetadata = useAuthStore((state) => state.userMetadata);

  // 1) Cargar script + init base SOLO una vez
  useEffect(() => {
    if (typeof window === "undefined" || !PIXEL_ID) return;

    if (window.__META_PIXEL_BASE_INIT__) return;

    ensureFbqLoaded();

    //console.log("[Meta Pixel] Init base:", PIXEL_ID);
    window.fbq?.("init", PIXEL_ID); // solo pixel id, sin advanced matching
    window.__META_PIXEL_BASE_INIT__ = true;
  }, []);

  // 2) PageView SOLO cuando cambia la ruta (SPA)
  useEffect(() => {
    if (typeof window.fbq === "function") {
      window.fbq("track", "PageView");
      //console.log("[Meta Pixel] PageView:", pathname + "?" + searchParams.toString());
    }
  }, [pathname, searchParams]);

  // 3) Init con advanced matching, evitando duplicados
  useEffect(() => {
    if (typeof window === "undefined" || typeof window.fbq !== "function" || !PIXEL_ID) return;

    const advanced = buildAdvancedMatching(userData, userMetadata);
    if (!advanced) return;

    const payloadKey = JSON.stringify(advanced);
    if (window.__META_PIXEL_LAST_AM__ === payloadKey) {
      //console.log("[Meta Pixel] Skipping duplicate AM init");
      return;
    }

    window.__META_PIXEL_LAST_AM__ = payloadKey;

    //console.log("[Meta Pixel] Init with Advanced Matching:", advanced);
    window.fbq("init", PIXEL_ID, advanced);
  }, [userData, userMetadata]);

  return null;
}